import React, { useCallback, useEffect } from 'react'
import { ToastOptions, toast } from 'react-toastify'

import { TypesenseLotDetail } from '@bonhams1793/bonhams-typescript'

import { decodeHtml } from '@nx/decode-html'
import { CloseIcon } from '@nx/fire/assets'
import { formatNumberWithCommas } from '@nx/helpers'
import { getTranslate } from '@nx/translations'
import { useUserInteraction } from '@nx/utils/google-tag-manager'

import {
  StyledCloseButton,
  StyledContent,
  StyledContentMessages,
  StyledHeading,
  StyledIconContainer,
  StyledImage,
  StyledIncreaseBid,
  StyledLabel,
  StyledLastBid,
  StyledNextBid,
  StyledToastContainer,
} from './OutbidToast.styles'
import translate from './OutbidToast.translate.json'

const { text } = getTranslate(translate)

interface ShowOutbidToastProps extends OutbidToastProps {
  toastOptions?: ToastOptions
}

interface OutbidToastProps {
  lot: TypesenseLotDetail
  outbidValue: number
  nextIncrementValue: number
  onIncreaseBidClick: () => void
}

export const OutbidToast = ({
  lot,
  outbidValue,
  nextIncrementValue,
  onIncreaseBidClick,
}: OutbidToastProps) => {
  const { recordInteraction } = useUserInteraction()

  const onCTAClick = useCallback(() => {
    recordInteraction({
      action: 'increaseBid.click',
      category: 'Outbid Notification - Global Toast',
      label: `${lot.auctionId}-${lot.lotId}`,
    })

    onIncreaseBidClick()

    toast.dismiss(lot.lotUniqueId)
  }, [
    lot.auctionId,
    lot.lotId,
    lot.lotUniqueId,
    onIncreaseBidClick,
    recordInteraction,
  ])

  useEffect(() => {
    recordInteraction({
      action: 'outbidNotif.display',
      category: 'Outbid Notification - Global Toast',
      label: `${lot.auctionId}-${lot.lotId}`,
    })
  }, [])

  return (
    <StyledToastContainer>
      <StyledIconContainer>
        <StyledImage
          src={
            lot.images.length > 0
              ? `${lot.images[0].url}&${[lot.images[0].URLParams, 'width=220'].filter(Boolean).join('&')}`
              : ''
          }
          alt={
            (lot.images.length > 0 && lot.images[0].caption) ||
            'Lot cover image'
          }
          $hasImage={Boolean(lot.images.length > 0)}
          height={110}
          width={110}
        />
      </StyledIconContainer>
      <StyledContent>
        <StyledLabel $level="XS">{text('outbidToast.outbid')}</StyledLabel>
        <div>
          <StyledHeading>
            {text('outbidToast.youHaveBeenOutbid', {
              lotId: lot.lotId,
              lotTitle: lot.title,
            })}
          </StyledHeading>
          <StyledContentMessages>
            <StyledLastBid $level="S">
              {text('outbidToast.yourLastBidWas', {
                lastBid: `${decodeHtml(lot.price.currencySymbol)}${formatNumberWithCommas(outbidValue)}`,
              })}
            </StyledLastBid>
            {' | '}
            <StyledNextBid $level="S">
              {text('outbidToast.yourNextBidCouldBe', {
                nextBid: `${decodeHtml(lot.price.currencySymbol)}${formatNumberWithCommas(nextIncrementValue)}`,
              })}
            </StyledNextBid>
          </StyledContentMessages>
        </div>
        <StyledIncreaseBid variant="label" onClick={onCTAClick}>
          {text('outbidToast.increaseYourBid')}
        </StyledIncreaseBid>
      </StyledContent>
    </StyledToastContainer>
  )
}

export const showOutbidToast = ({
  lot,
  outbidValue,
  nextIncrementValue,
  onIncreaseBidClick,
  toastOptions,
}: ShowOutbidToastProps) => {
  const options: CustomToastOptions = {
    autoClose: false,
    closeButton: ({ closeToast }) => (
      <StyledCloseButton onClick={closeToast}>
        <CloseIcon title="Close" />
      </StyledCloseButton>
    ),
    className: 'notification-width-auto-border-radius',
    toastId: lot.lotUniqueId,
    ...toastOptions,
  }

  const Component = (
    <OutbidToast
      lot={lot}
      outbidValue={outbidValue}
      nextIncrementValue={nextIncrementValue}
      onIncreaseBidClick={onIncreaseBidClick}
    />
  )

  toast(Component, options)

  toast.update(options.toastId, {
    render: Component,
    ...options,
  })
}

type CustomToastOptions = ToastOptions & Required<Pick<ToastOptions, 'toastId'>>
